import React from 'react';
import { usePage } from '@inertiajs/react';

import EventCard, { type EventItem } from '@/components/public/cards/EventCard';
import EventListView from '@/components/public/sections/EventListView';
import PageHero from '@/components/public/sections/PageHero';
import Seo from '@/components/public/Seo';
import { parishName, useSite } from '@/hooks/useSite';
import CategoryFilter from '@/components/public/ui/CategoryFilter';
import MonthSelector from '@/components/public/ui/MonthSelector';
import Section from '@/components/public/ui/Section';
import ViewToggle from '@/components/public/ui/ViewToggle';
import { EventsPageDetails } from '@/data/pages/events/details';
import { eventsForMonth } from '@/data/pages/events/helpers';
import { trackUmamiEvent } from '@/lib/analytics';

type Props = {
  events: EventItem[];
  categories: string[];
};

const ViewOptions = [
  { label: 'Grelha', value: 'grid', icon: 'grid_view' },
  { label: 'Lista', value: 'list', icon: 'view_list' },
];

export default function Events() {
  const { events, categories } = usePage<Props>().props;
  const site = useSite();

  const now = new Date();
  const [activeCategories, setActiveCategories] = React.useState<string[]>(['Todos']);
  const [currentMonth, setCurrentMonth] = React.useState(now.getMonth());
  const [currentYear, setCurrentYear] = React.useState(now.getFullYear());
  const [viewMode, setViewMode] = React.useState('grid');

  const filteredEvents = React.useMemo(() => {
    const monthEvents = eventsForMonth(events, currentMonth, currentYear);
    if (activeCategories.includes('Todos')) return monthEvents;
    return monthEvents.filter((e) => e.category && activeCategories.includes(e.category));
  }, [events, activeCategories, currentMonth, currentYear]);

  const filteredEventCount = (cats: string[], month: number, year: number) => {
    const monthEvents = eventsForMonth(events, month, year);
    if (cats.includes('Todos')) return monthEvents.length;
    return monthEvents.filter((e) => e.category && cats.includes(e.category)).length;
  };

  const handleMonthChange = (month: number, year: number) => {
    setCurrentMonth(month);
    setCurrentYear(year);
    trackUmamiEvent('events_filter', {
      categories: activeCategories.join(','),
      month: month + 1,
      year,
      result_count: filteredEventCount(activeCategories, month, year),
    });
  };

  const handleCategoryChange = (cats: string[]) => {
    setActiveCategories(cats);
    trackUmamiEvent('events_filter', {
      categories: cats.join(','),
      month: currentMonth + 1,
      year: currentYear,
      result_count: filteredEventCount(cats, currentMonth, currentYear),
    });
  };

  const handleViewChange = (nextViewMode: string) => {
    setViewMode(nextViewMode);
    trackUmamiEvent('events_view_change', { view_mode: nextViewMode });
  };

  return (
    <>
      <Seo
        title="Agenda"
        description={`Participe ativamente na vida da ${parishName(site)}. Descubra eventos culturais, sessões públicas e atividades para todas as gerações.`}
      />
      <PageHero
        badge={EventsPageDetails.badge}
        title={EventsPageDetails.title}
        accentWord={EventsPageDetails.accentWord}
        subtitle={EventsPageDetails.subtitle}
      />
      <Section spacing="md" aria-labelledby="events-heading">
        <h2 id="events-heading" className="sr-only">
          Lista de eventos
        </h2>
        <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <CategoryFilter
            categories={categories}
            active={activeCategories}
            onChange={handleCategoryChange}
          />
          <div className="flex flex-wrap items-center gap-4">
            <MonthSelector month={currentMonth} year={currentYear} onChange={handleMonthChange} />
            <ViewToggle
              options={ViewOptions}
              active={viewMode}
              onChange={handleViewChange}
              variant="underline"
            />
          </div>
        </div>
        {filteredEvents.length > 0 ? (
          viewMode === 'list' ? (
            <EventListView events={filteredEvents} />
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {filteredEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )
        ) : (
          <div className="surface-card p-12 text-center">
            <p className="text-on-surface-light text-lg">
              Não há eventos programados para este mês.
            </p>
          </div>
        )}
      </Section>
    </>
  );
}
